import { useEffect, useRef, useState } from "react";
import type { Props, SelectOptionValue } from "./select.js";
import { useSelectPosition } from "./use-select-position.js";

export const useSelectLogic = <T extends SelectOptionValue = string>({
  options,
  value,
  onChange,
  disabled,
  multiple,
  clearable,
  openUpward,
}: Pick<
  Props<T>,
  "options" | "value" | "onChange" | "disabled" | "multiple" | "clearable" | "openUpward"
>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isEmpty = (v: unknown) => v == null || v === "";
  const selectedValues = (Array.isArray(value) ? value : [value])
    .filter((v) => !isEmpty(v))
    .map((v) => String(v));

  const selectedOptions = options.filter((opt) => selectedValues.includes(String(opt.value)));

  const coords = useSelectPosition(isOpen, selectRef, dropdownRef, openUpward);

  const toggleOpen = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(undefined);
  };

  const handleSelect = (optionValueStr: string) => {
    const option = options.find((opt) => String(opt.value) === optionValueStr);
    if (!option) return;

    const optionValue = option.value;

    if (multiple) {
      const currentValues: T[] = Array.isArray(value) ? value : value != null ? [value] : [];
      const isSelected = currentValues.some((v) => v === optionValue);
      const newValues = isSelected
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue];

      onChange?.(newValues);
    } else {
      const isSelected = value === optionValue;

      if (isSelected && clearable) {
        onChange?.(undefined);
      } else {
        onChange?.(optionValue);
      }

      if (!isSelected || !clearable) {
        setIsOpen(false);
      }
    }
    if (!multiple) {
      setTimeout(() => selectRef.current?.focus(), 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    switch (e.key) {
      case "Enter":
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          if (highlightedIndex >= 0 && highlightedIndex < options.length) {
            handleSelect(String(options[highlightedIndex]?.value));
          }
        } else {
          return;
        }
        break;

      case "ArrowDown":
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : prev));
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        }
        break;

      case "Escape":
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(false);
          selectRef.current?.focus();
        }
        break;

      case "Tab":
        if (isOpen) {
          setIsOpen(false);
        }
        break;
    }
  };

  useEffect(() => {
    if (isOpen) {
      const firstSelectedIndex = options.findIndex((opt) =>
        selectedValues.includes(String(opt.value))
      );
      setHighlightedIndex(firstSelectedIndex >= 0 ? firstSelectedIndex : 0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && highlightedIndex >= 0) {
      const optionElement = document.getElementById(`select-option-${highlightedIndex}`);
      if (optionElement) {
        optionElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideTrigger = selectRef.current && !selectRef.current.contains(target);
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target);

      if (isOutsideTrigger && isOutsideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  return {
    isOpen,
    highlightedIndex,
    selectRef,
    dropdownRef,
    coords,
    selectedValues,
    selectedOptions,
    toggleOpen,
    handleClearAll,
    handleSelect,
    handleKeyDown,
  };
};
